import {
  AvatarProps,
  Icon,
  SideDrawer,
} from "@inductiveautomation/ws-components";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { TrainingDetails } from "./api/training.api";
import { TranslateKey } from "../../i18n/i18n.types";
import { getCurrentActions } from "./Training.actionsForState";
import {
  Certification,
  CertificationActionCallback,
  ElectiveWithType,
} from "./Training.types";
import { retrieveChipAttributes } from "./data/Training.current";
import CourseContent from "./drawer/CourseContent";
import EarnedContent from "./drawer/EarnedContent";
import InProgressContent from "./drawer/InProgressContent";

const actionsForCertification = (
  t: TFunction,
  onAction: CertificationActionCallback,
  certification: Certification | undefined,
  canPurchaseOnline: boolean
) => {
  if (!certification) return [];
  return getCurrentActions(t, onAction, certification, canPurchaseOnline);
};

const footerChipForCertification = (
  t: TFunction,
  selectedCertification: Certification
) => {
  return {
    id: "certification-status",
    variant: "status" as const,
    ...retrieveChipAttributes({ t, status: selectedCertification.status }),
  };
};

const isCourse = (
  certification: Certification
): certification is ElectiveWithType =>
  ["ELECTIVE", "EMPLOYEE"].includes(certification.type);

const TrainingDrawerContent = ({
  certification,
  onAction,
  canPurchaseOnline,
}: {
  certification: Certification | undefined;
  onAction: CertificationActionCallback;
  canPurchaseOnline: boolean;
}) => {
  if (!certification) return null;

  if (isCourse(certification)) {
    return <CourseContent certification={certification} onAction={onAction} />;
  }

  if (
    [
      "EARNED",
      "UPDATE_AVAILABLE",
      "EXPIRES_SOON",
      "EXPIRED",
      "INVALID",
    ].includes(certification.status) ||
    certification.type === "CREDENTIAL"
  ) {
    return (
      <EarnedContent
        certification={certification}
        onAction={onAction}
        canPurchaseOnline={canPurchaseOnline}
      />
    );
  }
  if (certification.status.startsWith("IN_PROGRESS")) {
    return (
      <InProgressContent certification={certification} onAction={onAction} />
    );
  }
};

const overlineForType: Record<Certification["type"], TranslateKey> = {
  CREDENTIAL: "training.drawer.overline.credential",
  CORE: "training.drawer.overline.certification",
  GOLD: "training.drawer.overline.certification",
  ELECTIVE: "training.drawer.overline.elective_course",
  EMPLOYEE: "training.drawer.overline.employee_course",
};

const headerIconProps = (certification: Certification | undefined) => {
  if (!certification || certification.type === "CREDENTIAL") {
    return { icon: "credential" as const };
  }

  if (isCourse(certification)) {
    if (certification.icon) {
      return { iconComponent: <Icon name={certification.icon} /> };
    } else {
      return { icon: "course" as const };
    }
  }

  return { icon: "certification" as const };
};

const hideCoreBasedOnGold = (certification: Certification) => {
  if (certification.type !== "CORE") {
    return false;
  }

  return (
    certification.goldStatus === "EARNED" ||
    certification.goldStatus === "EXPIRES_SOON"
  );
};

const TrainingDrawer = ({
  selectedCertification,
  trainingData,
  onAction,
  onClose,
  canPurchaseOnline,
}: {
  selectedCertification: Certification | undefined;
  trainingData: TrainingDetails | undefined;
  onAction: CertificationActionCallback;
  onClose: () => void;
  canPurchaseOnline: boolean;
}) => {
  const { t } = useTranslation();

  if (!selectedCertification || !trainingData) {
    return null;
  }

  selectedCertification.goldStatus =
    trainingData.certifications?.GOLD.status ?? "";

  // TS-1841 hide drawer footer for Core
  const shouldHideFooterForCore = hideCoreBasedOnGold(selectedCertification);

  const actions = actionsForCertification(
    t,
    onAction,
    selectedCertification,
    canPurchaseOnline
  );

  const footerChip = footerChipForCertification(t, selectedCertification);

  const footerProps = !shouldHideFooterForCore
    ? {
        actionsLabel: t("common.actions"),
        actions,
        chip: footerChip,
      }
    : undefined;

  const retrieveAvatarPropsByType = () => {
    if (isCourse(selectedCertification)) {
      // Courses surface their `icon` via headerIconProps's iconComponent path;
      // this branch only runs as a fallback when no icon is set.
      return {
        icon: "course",
        iconAvatarColor: "empty",
      };
    }
    if (selectedCertification.type === "CREDENTIAL") {
      return selectedCertification.status === "EARNED"
        ? {
            imageUrl: selectedCertification.iconUrl,
            imageUrlRemoveBorderRadius: true,
            variant: "image",
          }
        : {
            icon: "credential",
            iconAvatarColor: "empty",
          };
    }
    // CORE / GOLD: mirror the grid (data/Training.current.tsx#retrieveAvatarProps)
    // so EXPIRES_SOON / EXPIRED / INVALID / UPDATE_AVAILABLE still show the badge.
    const showImage =
      !!selectedCertification.iconUrl &&
      !["IN_PROGRESS", "NOT_STARTED", "UNEARNED"].includes(
        selectedCertification.status
      );
    return showImage
      ? {
          imageUrl: selectedCertification.iconUrl,
          imageUrlRemoveBorderRadius: true,
          variant: "image",
        }
      : {
          icon: "certification",
          iconAvatarColor: "empty",
        };
  };

  const retrieveHeading = () => {
    if (selectedCertification.type === "CREDENTIAL") {
      return t("training.headings.credential_sub_heading");
    } else if (selectedCertification.type === "CORE") {
      return t("training.headings.core_certification_sub_heading");
    } else {
      return t("training.headings.gold_certification_sub_heading");
    }
  };
  return (
    <SideDrawer
      testId="training-drawer"
      contentSpacing="fullWidth"
      open={!!selectedCertification}
      onClose={onClose}
      variant="modal"
      hideBackdrop
      headerProps={{
        ...headerIconProps(selectedCertification),
        overline: t(overlineForType[selectedCertification.type]),
        heading: retrieveHeading(),
        avatarProps: {
          ...(retrieveAvatarPropsByType() as AvatarProps),
        },
        showCloseButton: true,
      }}
      footerProps={footerProps}
    >
      <TrainingDrawerContent
        certification={selectedCertification}
        onAction={onAction}
        canPurchaseOnline={canPurchaseOnline}
      />
    </SideDrawer>
  );
};

export default TrainingDrawer;
